import React, { useState, useEffect } from 'react';
import { View, StyleSheet, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ChatBubble, ChatAvatar, Text } from '../../../components/ui';
import { Theme } from '../../../utils/theme';
import { ChatMessage } from '../../../contexts/MentorChatContext';
import RecommendationPill from './RecommendationPill';
import ProfileUpdateCard from './ProfileUpdateCard';
import { BookService } from '../../../services/bookService';
import { BookRepository } from '../../../repositories/bookRepository';
import { FireBook, FireInsight } from '../../../models/FirestoreModels';

interface MessageBubbleProps {
  message: ChatMessage;
  userInitials?: string;
  onNavigateToItem?: (type: 'book' | 'insight', id: string, bookId?: string) => void;
  showAvatar?: boolean;
}

interface BookData {
  id: string;
  title: string;
  coverImageUrl?: string;
}

interface InsightData {
  id: string;
  title: string;
  bookId?: string;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  userInitials,
  onNavigateToItem,
  showAvatar = true,
}) => {
  const [bookData, setBookData] = useState<Record<string, BookData>>({});
  const [insightData, setInsightData] = useState<Record<string, InsightData>>({});
  const [loading, setLoading] = useState(false);

  const isUser = message.role === 'user';
  const hasRecommendations = message.recommendations && 
    ((message.recommendations.books?.length || 0) > 0 || 
     (message.recommendations.insights?.length || 0) > 0);
  const hasProfileUpdates = message.profileUpdates && 
    (message.profileUpdates.coreProfile || message.profileUpdates.activeContext);

  // Display all items (removed 3-item limit)
  const displayBooks = message.recommendations?.books || [];
  const displayInsights = message.recommendations?.insights || [];

  // Fetch real book and insight data when message has recommendations
  useEffect(() => {
    if (!hasRecommendations || loading) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        // Collect all unique book IDs from both books and insights (filter out undefined)
        const bookIdsFromBooks = displayBooks.map(b => b.bookId).filter((id): id is string => !!id);
        const bookIdsFromInsights = displayInsights.map(i => i.bookId).filter((id): id is string => !!id);
        const uniqueBookIds = [...new Set([...bookIdsFromBooks, ...bookIdsFromInsights])];
        
        if (uniqueBookIds.length === 0) {
          // No books to fetch - insights will be fetched individually
          setLoading(false);
          return;
        }
        
        // Fetch all books in one batch (uses cache!)
        const allBooksPromises = uniqueBookIds.map(async (bookId: string) => {
          try {
            const result = await BookService.getBookDetail(bookId, 'en');
            if (result.success && result.data) {
              return {
                id: bookId,
                bookData: {
                  id: bookId,
                  title: result.data.book.title,
                  coverImageUrl: result.data.book.coverImageUrl || result.data.book.coverImageFullUrl,
                },
                insights: result.data.insights || [],
              };
            }
          } catch (error) {
            console.error(`Error fetching book ${bookId}:`, error);
          }
          return null;
        });

        const allBooksResults = await Promise.all(allBooksPromises);
        const newBookData: Record<string, BookData> = {};
        const insightsFromBooks: Record<string, FireInsight> = {};
        
        // Collect books and extract insights
        allBooksResults.forEach(result => {
          if (result) {
            newBookData[result.id] = result.bookData;
            // Store all insights by ID for quick lookup (both full and normalized)
            result.insights.forEach(insight => {
              const normalizedId = insight.id.includes('_') ? insight.id.split('_')[0] : insight.id;
              insightsFromBooks[insight.id] = insight;
              insightsFromBooks[normalizedId] = insight;
            });
          }
        });
        

        // Process insight data - fetch missing ones directly by ID
        const insightPromises = displayInsights.map(async (insight) => {
          try {
            // Normalize insight ID (remove language suffix if present)
            const normalizedInsightId = insight.insightId?.includes('_') 
              ? insight.insightId.split('_')[0] 
              : insight.insightId;
            
            // Check if we already have this insight from book details
            let foundInsight = normalizedInsightId 
              ? (insightsFromBooks[insight.insightId] || insightsFromBooks[normalizedInsightId])
              : undefined;
            
            // If not found in loaded books, fetch insight directly by ID to get title + bookId
            if (!foundInsight && normalizedInsightId) {
              try {
                const directInsight = await BookRepository.getInsightById(normalizedInsightId, 'en');
                if (directInsight) {
                  foundInsight = directInsight;
                  // Cache it for next time
                  insightsFromBooks[insight.insightId] = directInsight;
                  insightsFromBooks[normalizedInsightId] = directInsight;
                }
              } catch (err) {
                console.warn(`[MessageBubble] Failed to fetch insight ${normalizedInsightId}:`, err);
              }
            }
            
            if (foundInsight) {
              return {
                id: insight.insightId,
                data: {
                  id: insight.insightId,
                  title: foundInsight.title,
                  bookId: foundInsight.bookId || insight.bookId,
                }
              };
            }
            
            // Fallback: use reason as title (better than "Step X")
            return {
              id: insight.insightId,
              data: {
                id: insight.insightId,
                title: insight.reason || `Step ${insight.insightId}`,
                bookId: insight.bookId,
              }
            };
          } catch (error) {
            console.error(`Error processing insight ${insight.insightId}:`, error);
            return {
              id: insight.insightId,
              data: {
                id: insight.insightId,
                title: insight.reason || `Step ${insight.insightId}`,
                bookId: insight.bookId,
              }
            };
          }
        });

        const insightResults = await Promise.all(insightPromises);
        const newInsightData: Record<string, InsightData> = {};
        insightResults.forEach(result => {
          if (result && result.data) {
            newInsightData[result.id] = result.data;
          }
        });

        setBookData(newBookData);
        setInsightData(newInsightData);
      } catch (error) {
        console.error('Error fetching recommendation data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [hasRecommendations, displayBooks.length, displayInsights.length]);

  const handleItemPress = (type: 'book' | 'insight', id: string) => {
    // For insights, pass the fetched bookId if available
    if (type === 'insight' && insightData[id]?.bookId) {
      onNavigateToItem?.(type, id, insightData[id].bookId);
    } else {
      onNavigateToItem?.(type, id);
    }
  };

  const getBookTitle = (bookId: string): string => {
    return bookData[bookId]?.title || `Book ${bookId}`;
  };

  const getBookCoverUrl = (bookId: string): string | undefined => {
    return bookData[bookId]?.coverImageUrl;
  };

  const getInsightTitle = (insightId: string): string => {
    return insightData[insightId]?.title || `Insight ${insightId}`;
  };

  return (
    <View style={[styles.container, isUser ? styles.userContainer : styles.assistantContainer]}>
      {/* Avatar (only for assistant/mentor and only on last message in sequence) */}
      {!isUser && showAvatar && (
        <View style={styles.avatarContainer}>
          <ChatAvatar type="mentor" size="medium" />
        </View>
      )}
      
      {/* Spacer when avatar is hidden to maintain alignment */}
      {!isUser && !showAvatar && (
        <View style={styles.avatarSpacer} />
      )}

      {/* Message content */}
      <View style={[styles.messageContainer, isUser && styles.userMessageContainer]}>
        <ChatBubble
          role={message.role}
          content={message.content}
          timestamp={message.timestamp}
          isStreaming={message.isStreaming}
        >
          {/* Recommendations within the bubble */}
          {hasRecommendations && (
            <View style={styles.recommendationsContainer}>
              {/* Book recommendations */}
              {displayBooks.map((book, index) => (
                <RecommendationPill
                  key={`book-${book.bookId}-${index}`}
                  type="book"
                  id={book.bookId}
                  title={getBookTitle(book.bookId)}
                  reason={book.reason}
                  coverImageUrl={getBookCoverUrl(book.bookId)}
                  onPress={handleItemPress}
                />
              ))}
              
              {/* Insight recommendations with Play All */}
              {displayInsights.length > 0 && (
                <View style={styles.insightsHeader}>
                  <Text style={styles.insightsCount}>
                    {displayInsights.length} {displayInsights.length === 1 ? 'Insight' : 'Insights'}
                  </Text>
                  <Pressable
                    style={styles.playAllButton}
                    onPress={() => {
                      // Navigate to first insight (existing logic creates playlist)
                      const firstInsightId = displayInsights[0]?.insightId.toString();
                      if (firstInsightId) {
                        handleItemPress('insight', firstInsightId);
                      }
                    }}
                  >
                    <Ionicons name="play" size={14} color="#fff" />
                    <Text style={styles.playAllText}>Play All</Text>
                  </Pressable>
                </View>
              )}
              {displayInsights.map((insight, index) => (
                <RecommendationPill
                  key={`insight-${insight.insightId}-${index}`}
                  type="insight"
                  id={insight.insightId}
                  title={getInsightTitle(insight.insightId)}
                  reason={insight.reason}
                  onPress={handleItemPress}
                />
              ))}
            </View>
          )}

          {/* Profile updates card */}
          {hasProfileUpdates && (
            <ProfileUpdateCard updates={message.profileUpdates!} />
          )}
        </ChatBubble>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    marginVertical: Theme.spacing.xs,
    paddingHorizontal: Theme.spacing.lg,
    gap: Theme.spacing.sm,
  },
  userContainer: {
    flexDirection: 'row-reverse',
    paddingLeft: Theme.spacing.xxxxl, // More space on left for user messages
  },
  assistantContainer: {
    flexDirection: 'row',
    paddingRight: Theme.spacing.xxxxl, // More space on right for assistant messages
  },
  avatarContainer: {
    alignSelf: 'flex-end', // Align to bottom of message
  },
  avatarSpacer: {
    width: 40, // Same width as medium avatar to maintain spacing
  },
  messageContainer: {
    flex: 1,
    maxWidth: '85%', // Limit width for better readability
  },
  userMessageContainer: {
    alignItems: 'flex-end', // Align user messages to the right
  },
  recommendationsContainer: {
    gap: Theme.spacing.sm,
    marginTop: Theme.spacing.xs,
  },
  insightsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: Theme.spacing.xs,
    paddingHorizontal: Theme.spacing.xs,
  },
  insightsCount: {
    fontSize: 13,
    fontFamily: Theme.typography.fontFamily.medium,
    color: Theme.colors.textSecondary,
  },
  playAllButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    backgroundColor: Theme.colors.primary,
    borderRadius: Theme.borderRadius.round,
  },
  playAllText: {
    fontSize: 12,
    fontFamily: Theme.typography.fontFamily.medium,
    color: '#fff',
  },
});

// Memoize to prevent unnecessary re-renders
export default React.memo(MessageBubble, (prevProps: MessageBubbleProps, nextProps: MessageBubbleProps) => {
  // Return true if props are equal (skip re-render)
  // Note: We ignore onNavigateToItem callback to prevent re-renders on scroll state changes
  const prev = prevProps.message;
  const next = nextProps.message;
  
  return (
    prev.id === next.id &&
    prev.content === next.content &&
    prev.isStreaming === next.isStreaming &&
    prevProps.showAvatar === nextProps.showAvatar &&
    prev.recommendations?.books?.length === next.recommendations?.books?.length &&
    prev.recommendations?.insights?.length === next.recommendations?.insights?.length &&
    // Only check if profileUpdates presence changed, not deep equality
    !!prev.profileUpdates === !!next.profileUpdates
  );
}); 